import React, { useState } from "react";
import API from "./api";
import { GoogleLogin } from "@react-oauth/google";

function Login({
  onLogin,
  embedded = false,
  onCancel = null,
  title = "Login to your account",
  subtitle = "Continue to Jain Kalyanam matrimonial workspace.",
  eyebrow = "Welcome back",
  showHeader = true,
  submitLabel = "Login",
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [pendingProvider, setPendingProvider] = useState("");
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState("");

  const saveSession = (data) => {
    setRoleOptions([]);
    setSelectedRoleId("");
    setPendingProvider("");
    setPendingGoogleCredential("");
    onLogin(data);
  };

  const clearRoleSelection = () => {
    setRoleOptions([]);
    setSelectedRoleId("");
    setPendingProvider("");
    setPendingGoogleCredential("");
  };

  const awaitingRoleSelection = roleOptions.length > 0;

  const handleRoleSelectionResponse = (data, provider) => {
    if (!data?.requires_role_selection) {
      saveSession(data);
      return true;
    }

    const nextRoles = Array.isArray(data.roles) ? data.roles : [];
    setRoleOptions(nextRoles);
    setSelectedRoleId(String(nextRoles[0]?.id || ""));
    setPendingProvider(provider);
    return false;
  };

  const login = async (roleIdOverride = null) => {
    setError("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail || !password) {
      setError("Please enter email and password.");
      return;
    }

    setLoading(true);

    try {
      const res = await API.post("/auth/login", {
        email: cleanEmail,
        password,
        ...(roleIdOverride ? { role_id: Number(roleIdOverride) } : {}),
      });

      handleRoleSelectionResponse(res.data, "password");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Invalid email or password."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setGoogleLoading(true);

    try {
      const credential = credentialResponse.credential;
      const res = await API.post("/auth/google", {
        credential,
      });

      if (!handleRoleSelectionResponse(res.data, "google")) {
        setPendingGoogleCredential(credential);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Google login failed."
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed. Please try again.");
  };

  const continueRoleSelection = async () => {
    setError("");

    if (!selectedRoleId) {
      setError("Please select a role to continue.");
      return;
    }

    if (pendingProvider === "google") {
      if (!pendingGoogleCredential) {
        setError("Google session expired. Please sign in again.");
        clearRoleSelection();
        return;
      }

      setGoogleLoading(true);

      try {
        const res = await API.post("/auth/google", {
          credential: pendingGoogleCredential,
          role_id: Number(selectedRoleId),
        });
        saveSession(res.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Google login failed."
        );
      } finally {
        setGoogleLoading(false);
      }

      return;
    }

    await login(selectedRoleId);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading && !googleLoading) {
      if (awaitingRoleSelection) {
        continueRoleSelection();
        return;
      }
      login();
    }
  };

  const cardContent = (
    <div style={{ ...styles.card, ...(embedded ? styles.cardEmbedded : {}) }}>
      <div style={styles.mobileLogo}>JK</div>

      {showHeader && (
        <div style={styles.cardHeader}>
          <p style={styles.cardEyebrow}>{eyebrow}</p>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.subtitle}>
            {awaitingRoleSelection
              ? "This account has multiple roles. Choose one to continue with role-specific access."
              : subtitle}
          </p>
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}

      {awaitingRoleSelection ? (
        <>
          <div style={styles.roleSelectBanner}>
            <strong style={styles.roleSelectTitle}>Choose active role</strong>
            <span style={styles.roleSelectText}>
              Your session will use only this role&apos;s permissions and policies.
            </span>
          </div>

          <div style={styles.roleOptionList}>
            {roleOptions.map((role) => {
              const isSelected = String(role.id) === String(selectedRoleId);

              return (
                <button
                  key={role.id}
                  type="button"
                  style={{
                    ...styles.roleOptionButton,
                    ...(isSelected ? styles.roleOptionButtonActive : {}),
                  }}
                  onClick={() => setSelectedRoleId(String(role.id))}
                >
                  <span style={styles.roleOptionName}>
                    {role.name || `Role #${role.id}`}
                  </span>
                  <span style={styles.roleOptionDesc}>
                    {role.description || "Use this role for the current login session."}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            style={{
              ...styles.button,
              ...((loading || googleLoading) ? styles.buttonDisabled : {}),
            }}
            onClick={continueRoleSelection}
            disabled={loading || googleLoading}
          >
            {loading || googleLoading ? "Continuing..." : "Continue with Selected Role"}
          </button>

          <button
            type="button"
            style={styles.cancelBtn}
            onClick={() => {
              clearRoleSelection();
              setError("");
            }}
            disabled={loading || googleLoading}
          >
            Start Over
          </button>
        </>
      ) : (
        <>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              if (pendingProvider) clearRoleSelection();
              setEmail(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="email"
          />

          <label style={styles.label}>Password</label>
          <div style={styles.passwordFieldWrap}>
            <input
              type={showPassword ? "text" : "password"}
              style={styles.passwordInput}
              placeholder="Enter password"
              value={password}
              onChange={(e) => {
                if (pendingProvider) clearRoleSelection();
                setPassword(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
            />
            <button
              type="button"
              style={styles.passwordToggleBtn}
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeIcon /> : <EyeOffIcon />}
            </button>
          </div>

          <button
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            onClick={() => login()}
            disabled={loading}
          >
            {loading ? "Logging in..." : submitLabel}
          </button>

          <div style={styles.divider}>
            <span style={styles.dividerLine} />
            <span style={styles.dividerText}>or continue with</span>
            <span style={styles.dividerLine} />
          </div>

          <div style={styles.googleWrapper}>
            {googleLoading ? (
              <div style={styles.googleLoading}>Connecting Google...</div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                text="continue_with"
                shape="pill"
                theme="outline"
                width="330"
              />
            )}
          </div>
        </>
      )}

      <p style={styles.securityNote}>
        Access is restricted to approved team members.
      </p>

      {embedded && onCancel && (
        <button type="button" style={styles.cancelBtn} onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );

  if (embedded) {
    return <div style={styles.embeddedContainer}>{cardContent}</div>;
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatSoft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>

      <div style={styles.backgroundGlowOne} />
      <div style={styles.backgroundGlowTwo} />

      <div style={styles.shell}>
        <div style={styles.leftPanel}>
          <div style={styles.brandBadge}>JK</div>
          <p style={styles.eyebrow}>Jain Kalyanam CRM</p>
          <h1 style={styles.heroTitle}>
            Manage meaningful matches with clarity.
          </h1>
          <p style={styles.heroText}>
            Search profiles, manage family details, track preferences, review
            duplicate signals, and handle matrimony workflows from one secure
            workspace.
          </p>

          <div style={styles.featureGrid}>
            <Feature label="Profile CRM" value="Smart search" />
            <Feature label="Duplicate Safety" value="Phonetic checks" />
            <Feature label="Team Access" value="Role based" />
          </div>
        </div>
        {cardContent}
      </div>
    </div>
  );
}

function Feature({ label, value }) {
  return (
    <div style={styles.featureCard}>
      <span style={styles.featureLabel}>{label}</span>
      <b style={styles.featureValue}>{value}</b>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 5.2A10.6 10.6 0 0 1 12 5c7 0 10.5 7 10.5 7a13.4 13.4 0 0 1-3.1 4.1M6.7 6.7C3.6 8.6 1.5 12 1.5 12s3.5 7 10.5 7c1.4 0 2.7-.28 3.9-.77M9.9 9.9a3 3 0 0 0 4.24 4.24"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Login;

const styles = {
  embeddedContainer: {
    width: "100%",
    margin: "0 auto",
  },
  container: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    padding: "28px 18px",
    background:
      "radial-gradient(circle at top left, rgba(132,42,45,0.16) 0, transparent 32%), linear-gradient(135deg, #ffffff 0%, #fffafa 55%, #fdf7f7 100%)",
    fontFamily: "var(--jm-font-body)",
    boxSizing: "border-box",
  },

  backgroundGlowOne: {
    position: "absolute",
    width: 340,
    height: 340,
    borderRadius: "50%",
    background: "rgba(132, 42, 45, 0.14)",
    filter: "blur(12px)",
    top: -120,
    right: -90,
    animation: "floatSoft 6s ease-in-out infinite",
  },

  backgroundGlowTwo: {
    position: "absolute",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "rgba(136, 45, 48, 0.10)",
    filter: "blur(12px)",
    bottom: -110,
    left: -80,
    animation: "floatSoft 7s ease-in-out infinite",
  },

  shell: {
    position: "relative",
    zIndex: 1,
    width: "min(1040px, 100%)",
    display: "grid",
    gridTemplateColumns: "1.1fr 430px",
    gap: 24,
    alignItems: "stretch",
    animation: "fadeUp 0.38s ease both",
  },

  leftPanel: {
    padding: 38,
    borderRadius: 32,
    background:
      "linear-gradient(135deg, rgba(132,42,45,0.98) 0%, rgba(136,45,48,0.94) 58%, rgba(95,31,34,0.86) 100%)",
    color: "#fff",
    boxShadow: "0 26px 80px rgba(132,42,45,0.28)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 520,
  },

  brandBadge: {
    width: 58,
    height: 58,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "#fff",
    fontWeight: 950,
    letterSpacing: 1,
    marginBottom: 28,
  },

  eyebrow: {
    margin: 0,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 2.8,
    fontWeight: 900,
    opacity: 0.82,
  },

  heroTitle: {
    maxWidth: 520,
    margin: "12px 0 0",
    fontSize: 46,
    lineHeight: 1.02,
    fontWeight: 700,
    letterSpacing: -1.2,
  },

  heroText: {
    maxWidth: 560,
    margin: "18px 0 0",
    color: "rgba(255,255,255,0.84)",
    fontSize: 15,
    lineHeight: 1.8,
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginTop: 34,
  },

  featureCard: {
    padding: 14,
    borderRadius: 18,
    background: "rgba(255,255,255,0.13)",
    border: "1px solid rgba(255,255,255,0.18)",
  },

  featureLabel: {
    display: "block",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    opacity: 0.76,
    marginBottom: 4,
    fontWeight: 900,
  },

  featureValue: {
    fontSize: 14,
  },

  card: {
    padding: 34,
    background: "rgba(255,255,255,0.92)",
    borderRadius: 32,
    boxShadow: "0 26px 80px rgba(132,42,45,0.12)",
    textAlign: "left",
    border: "1px solid rgba(132,42,45,0.16)",
    backdropFilter: "blur(16px)",
  },
  cardEmbedded: {
    width: "100%",
    margin: 0,
    padding: 0,
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backdropFilter: "none",
  },

  mobileLogo: {
    display: "none",
    width: 48,
    height: 48,
    borderRadius: 16,
    placeItems: "center",
    color: "#fff",
    background: "linear-gradient(135deg, #842A2D 0%, #882D30 100%)",
    fontWeight: 950,
    marginBottom: 18,
  },

  cardHeader: {
    marginBottom: 22,
  },

  cardEyebrow: {
    margin: 0,
    color: "#842A2D",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 2,
  },

  title: {
    margin: "8px 0 0",
    color: "#2f1113",
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: -0.55,
    lineHeight: 1.08,
  },

  subtitle: {
    margin: "8px 0 0",
    color: "#8a5a5c",
    fontSize: 14,
    lineHeight: 1.6,
  },

  errorBox: {
    background: "#fff4f4",
    border: "1px solid rgba(132,42,45,0.22)",
    color: "#842A2D",
    borderRadius: 16,
    padding: "12px 14px",
    marginBottom: 16,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  label: {
    display: "block",
    marginBottom: 7,
    color: "#842A2D",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.1,
  },

  input: {
    width: "100%",
    height: 46,
    padding: "0 14px",
    marginBottom: 14,
    borderRadius: 15,
    border: "1px solid rgba(132,42,45,0.18)",
    background: "#fff",
    color: "#2f1113",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },

  passwordFieldWrap: {
    position: "relative",
    marginBottom: 14,
  },

  passwordInput: {
    width: "100%",
    height: 46,
    padding: "0 44px 0 14px",
    marginBottom: 0,
    borderRadius: 15,
    border: "1px solid rgba(132,42,45,0.18)",
    background: "#fff",
    color: "#2f1113",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },

  passwordToggleBtn: {
    position: "absolute",
    top: 0,
    right: 0,
    height: 46,
    width: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "#842A2D",
    cursor: "pointer",
  },

  button: {
    width: "100%",
    height: 46,
    background: "linear-gradient(135deg, #842A2D 0%, #882D30 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 950,
    letterSpacing: 0.3,
    marginTop: 4,
    marginBottom: 20,
    fontSize: 14,
    boxShadow: "0 16px 34px rgba(132,42,45,0.26)",
  },

  buttonDisabled: {
    opacity: 0.62,
    cursor: "not-allowed",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },

  dividerLine: {
    flex: 1,
    height: 1,
    background: "rgba(132,42,45,0.20)",
    display: "block",
  },

  dividerText: {
    color: "#a47476",
    fontSize: 12,
    fontWeight: 800,
  },

  googleWrapper: {
    display: "flex",
    justifyContent: "center",
    minHeight: 42,
  },

  googleLoading: {
    width: "100%",
    height: 42,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "#fdf7f7",
    color: "#8a5a5c",
    fontSize: 13,
    fontWeight: 800,
    border: "1px solid rgba(132,42,45,0.16)",
  },
  roleSelectBanner: {
    display: "grid",
    gap: 4,
    padding: "14px 16px",
    borderRadius: 18,
    background: "#fff7f4",
    border: "1px solid rgba(132,42,45,0.14)",
    marginBottom: 14,
  },
  roleSelectTitle: {
    fontSize: 14,
    color: "#842A2D",
  },
  roleSelectText: {
    fontSize: 12,
    color: "#8a5a5c",
    lineHeight: 1.5,
  },
  roleOptionList: {
    display: "grid",
    gap: 10,
    marginBottom: 14,
  },
  roleOptionButton: {
    border: "1px solid rgba(132,42,45,0.14)",
    background: "#fff",
    borderRadius: 18,
    padding: "13px 14px",
    display: "grid",
    gap: 4,
    textAlign: "left",
    cursor: "pointer",
  },
  roleOptionButtonActive: {
    background: "#fff4f0",
    borderColor: "rgba(132,42,45,0.24)",
    boxShadow: "0 12px 26px rgba(132,42,45,0.10)",
  },
  roleOptionName: {
    fontSize: 14,
    fontWeight: 900,
    color: "#2f1113",
  },
  roleOptionDesc: {
    fontSize: 12,
    color: "#8a5a5c",
    lineHeight: 1.45,
  },

  securityNote: {
    margin: "18px 0 0",
    textAlign: "center",
    color: "#a47476",
    fontSize: 12,
    fontWeight: 700,
  },
  cancelBtn: {
    width: "100%",
    marginTop: 12,
    height: 44,
    borderRadius: 16,
    border: "1px solid rgba(132,42,45,0.18)",
    background: "#fff",
    color: "#6f3f43",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
};
